import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authenticated | unauthenticated

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
      setStatus("authenticated");
    } catch {
      setUser(null); setTenant(null);
      setStatus("unauthenticated");
    }
  };

  useEffect(() => {
    // CRITICAL: If returning from Emergent Google OAuth callback, skip /auth/me.
    // The GoogleAuthCallback component will exchange session_id and set the token first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("loading");
      return;
    }
    refresh();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("gacrf_token", data.access_token);
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("gacrf_token");
    setUser(null); setTenant(null); setStatus("unauthenticated");
  };

  return (
    <AuthContext.Provider value={{ user, tenant, status, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
